import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SubscriptionPlan } from '../types/subscription';
import { useLanguage } from '../i18n/LanguageContext';

// 定价页面容器
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

// 标题部分
const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 16px;
  color: #333;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #666;
  max-width: 700px;
  margin: 0 auto;
`;

// 计划卡片区域
const PlansContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
`;

const PlanCard = styled.div<{ popular?: boolean }>`
  flex: 1;
  min-width: 300px;
  max-width: 350px;
  border: ${({ popular }) => (popular ? '2px solid #4f46e5' : '1px solid #e5e7eb')};
  border-radius: 8px;
  padding: 30px;
  background-color: white;
  position: relative;
  box-shadow: ${({ popular }) => (popular ? '0 10px 20px rgba(79, 70, 229, 0.1)' : '0 4px 6px rgba(0, 0, 0, 0.05)')};
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ popular }) => 
      popular ? '0 15px 25px rgba(79, 70, 229, 0.15)' : '0 10px 15px rgba(0, 0, 0, 0.1)'};
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -12px;
  right: 20px;
  background-color: #4f46e5;
  color: white;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 12px;
`;

const PlanName = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 10px;
  color: #333;
`;

const PlanDescription = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 20px;
  min-height: 40px;
`;

const PriceContainer = styled.div`
  margin-bottom: 20px;
`;

const Price = styled.span`
  font-size: 2.5rem;
  font-weight: 700;
  color: #333;
`;

const PriceUnit = styled.span`
  font-size: 1rem;
  color: #666;
  margin-left: 4px;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 30px 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 0.9rem;
  color: #333;
`;

const FeatureIcon = styled.span<{ included: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${({ included }) => (included ? '#22c55e' : '#f87171')};
  color: white;
  margin-right: 10px;
  font-size: 0.7rem;
`;

const FeatureText = styled.span<{ included: boolean }>`
  text-decoration: ${({ included }) => (included ? 'none' : 'line-through')};
  color: ${({ included }) => (included ? '#333' : '#999')};
`;

const SubscribeButton = styled.button<{ primary?: boolean }>`
  width: 100%;
  padding: 12px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;
  border: ${({ primary }) => (primary ? 'none' : '1px solid #4f46e5')};
  background-color: ${({ primary }) => (primary ? '#4f46e5' : 'transparent')};
  color: ${({ primary }) => (primary ? 'white' : '#4f46e5')};

  &:hover {
    background-color: ${({ primary }) => (primary ? '#4338ca' : 'rgba(79, 70, 229, 0.05)')};
  }

  &:disabled {
    background-color: #d1d5db;
    cursor: not-allowed;
    color: #6b7280;
    border: 1px solid #d1d5db;
  }
`;

// 订阅表单
const SubscriptionForm = styled.form`
  width: 100%;
`;

const HiddenInput = styled.input`
  display: none;
`;

const MessageContainer = styled.div<{ type: 'info' | 'error' | 'success' }>`
  background-color: ${({ type }) => {
    switch (type) {
      case 'info':
        return '#e5f3ff';
      case 'error':
        return '#fef2f2';
      case 'success':
        return '#f0fdf4';
      default:
        return 'transparent';
    }
  }};
  border-left: 4px solid ${({ type }) => {
    switch (type) {
      case 'info':
        return '#4f46e5';
      case 'error':
        return '#ef4444';
      case 'success':
        return '#22c55e';
      default:
        return 'transparent';
    }
  }};
  padding: 10px;
  margin-bottom: 20px;
`;

const Pricing: React.FC = () => {
  const { t, language } = useLanguage();
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const { user, subscriptionStatus } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState<{ text: string; type: 'info' | 'error' | 'success' } | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  
  // 输出调试信息
  useEffect(() => {
    if (user && subscriptionStatus) {
      console.log('用户订阅状态:', {
        isActive: subscriptionStatus.isActive,
        status: subscriptionStatus.status,
        planId: subscriptionStatus.planId,
        periodEnd: subscriptionStatus.periodEnd ? new Date(subscriptionStatus.periodEnd).toLocaleString() : null,
        cancelAtPeriodEnd: subscriptionStatus.cancelAtPeriodEnd
      });
    }
  }, [user, subscriptionStatus]);
  
  // 从后端直接获取订阅状态
  useEffect(() => {
    const fetchSubscriptionData = async () => {
      if (!user) return;
      
      try {
        const response = await fetch(`https://get-user-subscription-4lwrmzn6aa-uc.a.run.app?user_id=${user.uid}`);
        
        if (!response.ok) {
          throw new Error(`服务器响应错误: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('后端返回的订阅数据:', data);
        
        if (data.exists && data.subscription) {
          setSubscriptionData(data.subscription);
        }
      } catch (error) {
        console.error('获取订阅信息失败:', error);
      }
    };

    if (user) {
      fetchSubscriptionData();
    }
  }, [user]);
  
  // 全面检查用户是否有有效订阅
  const hasValidSubscription = () => {
    // 方法1: 通过 AuthContext 中的 subscriptionStatus 检查
    if (subscriptionStatus && (
      subscriptionStatus.isActive || 
      subscriptionStatus.status === 'active' || 
      subscriptionStatus.status === 'active_canceling'
    )) {
      return true;
    }
    
    // 方法2: 通过直接从后端获取的订阅数据检查
    if (subscriptionData && (
      subscriptionData.status === 'active' || 
      subscriptionData.status === 'active_canceling' ||
      subscriptionData.subscription_status === 'active' ||
      subscriptionData.subscription_status === 'active_canceling'
    )) {
      return true;
    }
    
    return false;
  };
  
  // 使用translations中的plans数据
  const pricingPlans = t('pricing.plans', { returnObjects: true }) as Array<{
    name: string;
    price: string;
    features: string[];
    buttonText: string;
  }>;

  // 构建订阅计划
  const plans: SubscriptionPlan[] = [
    {
      id: 'free-trial',
      name: pricingPlans[0].name,
      description: language === 'zh' ? '零风险体验所有专业功能' : 'Try all Pro features risk-free',
      price: 0,
      currency: 'USD',
      interval: 'month',
      features: pricingPlans[0].features.map(feature => ({ 
        name: feature, 
        included: true 
      })),
      priceId: '',
      trial: true,
    },
    {
      id: 'pro-yearly',
      name: pricingPlans[1].name,
      description: t('plans.pro-yearly.description'),
      price: 9.9,
      currency: 'USD',
      interval: 'year',
      features: pricingPlans[1].features.map(feature => ({ 
        name: feature, 
        included: true 
      })),
      priceId: 'price_pro_yearly',
    },
    {
      id: 'pro-lifetime',
      name: pricingPlans[2].name,
      description: t('plans.pro-lifetime.description'),
      price: 19.9,
      currency: 'USD',
      interval: 'year',
      features: pricingPlans[2].features.map(feature => ({ 
        name: feature, 
        included: true 
      })),
      priceId: 'price_pro_lifetime',
      lifetime: true,
    },
  ];

  // 处理计划选择
  const handlePlanSelect = async (plan: SubscriptionPlan) => {
    if (plan.id === 'free-trial') {
      // 免费试用不需要结账
      // 这里可以添加试用激活逻辑
      return;
    }

    if (!user) {
      // 用户未登录，引导至登录页面
      navigate('/login', { state: { from: '/pricing' } });
      return;
    }
    
    // 使用全面的订阅状态检查函数
    if (hasValidSubscription()) {
      // 显示提示信息
      setMessage({
        text: language === 'zh' ? '您已有有效订阅，正在跳转到订阅管理页面...' : 'You already have an active subscription. Redirecting to subscription management...',
        type: 'info'
      });
      
      // 延迟一秒后跳转，让用户有时间看到提示信息
      setTimeout(() => {
        navigate('/mySubscription');
      }, 1000);
      return;
    }
    
    // 设置加载状态
    setIsLoading(plan.id);
    
    try {
      // 确定lookup_key
      let lookupKey;
      if (plan.id === 'pro-monthly') {
        lookupKey = 'pro_monthly';
      } else if (plan.id === 'pro-yearly') {
        lookupKey = 'pro_yearly';
      } else if (plan.id === 'pro-lifetime') {
        lookupKey = 'pro_lifetime';
      }
      
      // 构建成功和取消的重定向URL
      const successUrl = `${window.location.origin}/subscription-success`;
      const cancelUrl = `${window.location.origin}/subscription-canceled`;
      
      // 调用后端API创建结账会话
      const response = await fetch('https://create-checkout-session-4lwrmzn6aa-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lookup_key: lookupKey,
          success_url: successUrl,
          cancel_url: cancelUrl,
          user_id: user.uid,
        }),
      });
      
      if (!response.ok) {
        throw new Error(language === 'zh' ? '创建结账会话失败' : 'Failed to create checkout session');
      }
      
      // 解析响应获取结账URL
      const data = await response.json();
      
      // 检查响应中是否包含URL
      if (data && data.url) {
        // 重定向到Stripe结账页面
        window.location.href = data.url;
      } else {
        throw new Error(language === 'zh' ? '未获取到有效的结账URL' : 'Invalid checkout URL');
      }
    } catch (error) {
      console.error('结账错误:', error);
      setMessage({
        text: language === 'zh' ? '创建结账会话失败，请稍后再试' : 'Failed to create checkout session. Please try again later.',
        type: 'error'
      });
      setIsLoading(null);
    }
  };

  // 获取特定计划的价格显示
  const getPriceDisplay = (plan: SubscriptionPlan) => {
    if (plan.price === 0) {
      if (plan.trial) {
        return language === 'zh' ? '免费' : 'Free';
      }
      return t('pricing.free');
    }
    
    const currency = plan.currency === 'CNY' ? '¥' : '$';
    return `${currency}${plan.price}`;
  };

  // 获取特定计划的按钮文本
  const getButtonText = (plan: SubscriptionPlan) => {
    if (isLoading === plan.id) {
      return t('pricing.processing');
    }
    
    // 使用全面的订阅状态检查函数
    if (hasValidSubscription()) {
      return language === 'zh' ? '管理订阅' : 'Manage Subscription';
    }
    
    // 根据plan.id获取对应的索引
    let buttonIndex = 0;
    if (plan.id === 'free-trial') {
      buttonIndex = 0;
    } else if (plan.id === 'pro-yearly') {
      buttonIndex = 1;
    } else if (plan.id === 'pro-lifetime') {
      buttonIndex = 2;
    }
    
    return pricingPlans[buttonIndex].buttonText;
  };

  return (
    <Container>
      <HeaderSection>
        <Title>{t('pricing.title')}</Title>
        <Subtitle>
          {t('pricing.subtitle')}
        </Subtitle>
        
        {message && (
          <MessageContainer type={message.type}>
            {message.text}
          </MessageContainer>
        )}
      </HeaderSection>

      <PlansContainer>
        {plans.map((plan) => (
          <PlanCard key={plan.id} popular={plan.id === 'pro-yearly'}>
            {plan.id === 'pro-yearly' && <PopularBadge>{t('pricing.mostPopular')}</PopularBadge>}
            {plan.trial && <PopularBadge style={{ backgroundColor: '#22c55e' }}>{language === 'zh' ? '限时' : 'Limited Time'}</PopularBadge>}
            
            <PlanName>{plan.name}</PlanName>
            <PlanDescription>{plan.description}</PlanDescription>
            
            <PriceContainer>
              <Price>
                {getPriceDisplay(plan)}
              </Price>
              {plan.price > 0 && (
                <PriceUnit>
                  {plan.lifetime ? 
                    (language === 'zh' ? '/终身' : '/lifetime') : 
                    `/${plan.interval === 'month' ? t('pricing.mo') : t('pricing.yr')}`
                  }
                </PriceUnit>
              )}
            </PriceContainer>
            
            <FeaturesList>
              {plan.features.map((feature, index) => (
                <FeatureItem key={index}>
                  <FeatureIcon included={feature.included}>
                    {feature.included ? <FaCheck size={10} /> : <FaTimes size={10} />}
                  </FeatureIcon>
                  <FeatureText included={feature.included}>
                    {feature.name}
                  </FeatureText>
                </FeatureItem>
              ))}
            </FeaturesList>
            
            {plan.id === 'free-trial' ? (
              // 免费试用按钮
              <SubscribeButton
                primary={false}
                onClick={() => handlePlanSelect(plan)}
                disabled={isLoading === plan.id}
              >
                {getButtonText(plan)}
              </SubscribeButton>
            ) : (
              // 付费计划使用API调用代替表单提交
              <SubscribeButton
                primary={true}
                onClick={() => handlePlanSelect(plan)}
                disabled={isLoading === plan.id || !user}
              >
                {getButtonText(plan)}
              </SubscribeButton>
            )}
          </PlanCard>
        ))}
      </PlansContainer>
    </Container>
  );
};

export default Pricing; 